import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Box, Typography } from '@mui/material';
import CustomerConfirmationLayout from '../Components/CustomerConfirmationLayout';
import { formatOrgContactLine } from '../Shared/customerContact';
import { fetchPaymentContext } from '../Shared/publicApi';

const PaymentCancelled = () => {
	const [params] = useSearchParams();
	const bidId = (params.get('bid_id') || '').trim();
	const [data, setData] = useState(null);

	useEffect(() => {
		if (!bidId) {
			return;
		}

		let cancelled = false;
		(async () => {
			try {
				const result = await fetchPaymentContext(bidId);
				if (!cancelled) {
					setData(result);
				}
			} catch {
				// Generic copy below when context is unavailable
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [bidId]);

	const bodyCopy = useMemo(() => {
		if (data?.invoice_payment_active === false) {
			const org = data?.org_name;
			if (org) {
				return `This invoice is no longer valid. Contact ${org} for an updated invoice.`;
			}
			return 'This invoice is no longer valid. Contact the business for an updated invoice.';
		}
		const org = data?.org_name;
		if (org) {
			return `No payment was made. Use the link in your email to try again, or contact ${org} if you need help.`;
		}
		return 'No payment was made. Use the link in your email to try again.';
	}, [data?.invoice_payment_active, data?.org_name]);

	const contactLine = useMemo(
		() =>
			data
				? formatOrgContactLine({
						orgName: data.org_name,
						orgPhone: data.org_phone,
						orgEmail: data.org_email,
					})
				: null,
		[data],
	);

	return (
		<CustomerConfirmationLayout
			orgLogoUrl={data?.org_logo_url}
			orgName={data?.org_name}
			headline="Payment cancelled"
			icon={
				<CancelOutlinedIcon
					sx={{ fontSize: 56, color: 'warning.main' }}
					aria-hidden
				/>
			}
		>
			{data?.job_title && (
				<Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
					{data.job_title}
				</Typography>
			)}
			<Typography color="text.secondary" sx={{ mb: contactLine ? 2 : 0 }}>
				{bodyCopy}
			</Typography>
			{contactLine && (
				<Box sx={{ mt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						{contactLine}
					</Typography>
				</Box>
			)}
		</CustomerConfirmationLayout>
	);
};

export default PaymentCancelled;
